import { Fragment, type ReactElement } from 'react';
import { Button, Link } from 'react-aria-components';
import { footerStripPhotos, logos } from '../assets';
import { cx } from '../ui/cx';
import s from './AppFooter.module.css';

const VALUES = ['Creativity', 'Integrity', 'Passion', 'Responsiveness', 'Collaboration'] as const;

export interface AppFooterProps {
  /** Show the slowly drifting photo strip (still under reduced motion). Pass photos to choose them. */
  photoStrip?: boolean | readonly string[];
  /** Areté's awards policy (an Admin setting, https only). Without one the link is left out. */
  awardsPolicyHref?: string | null;
  /** Opens the Help panel, told which button opened it. Without it, Help links to `helpHref`. */
  onHelp?: (opener?: HTMLElement) => void;
  helpHref?: string;
}

/** Values band, then logo, "Discover. Develop. Deliver." and links. The shell pins it to the bottom. */
export function AppFooter({
  photoStrip = false,
  awardsPolicyHref = null,
  onHelp,
  helpHref = '/help',
}: AppFooterProps): ReactElement {
  const strip = photoStrip === true ? footerStripPhotos : photoStrip === false ? [] : photoStrip;
  return (
    <footer className={s.footer}>
      {strip.length > 0 && (
        <div className={s.strip} aria-hidden="true">
          <div className={s.drift}>
            {/* Two copies so the strip loops without a seam. */}
            {[...strip, ...strip].map((src, index) => (
              <img key={`${src}-${index}`} src={src} alt="" decoding="async" loading="lazy" />
            ))}
          </div>
          <div className={s.stripWash} />
        </div>
      )}
      <div className={s.values}>
        <p className={cx('disp', s.valuesList)}>
          {VALUES.map((value, index) => (
            <Fragment key={value}>
              {index > 0 && (
                <span className={s.dot} aria-hidden="true">
                  ·
                </span>
              )}
              <span>{value}</span>
            </Fragment>
          ))}
        </p>
      </div>
      <div className={s.bottom}>
        <img className={s.logo} src={logos.horizontalWhite} alt="Areté" width={105} height={28} />
        <p className={cx('eyebrow', s.tagline)}>Discover. Develop. Deliver.</p>
        <nav aria-label="Footer">
          <ul className={s.links}>
            {awardsPolicyHref !== null && (
              <li>
                {/* A plain link: it leaves MyRewards, so it never goes through the app's router. */}
                <a
                  href={awardsPolicyHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s.link}
                >
                  Awards policy <span className="visually-hidden">(opens in a new tab)</span>
                </a>
              </li>
            )}
            <li>
              {onHelp ? (
                <Button
                  className={s.link}
                  onPress={(event) => {
                    onHelp(event.target.closest('button') ?? undefined);
                  }}
                >
                  Help
                </Button>
              ) : (
                <Link href={helpHref} className={s.link}>
                  Help
                </Link>
              )}
            </li>
          </ul>
        </nav>
      </div>
    </footer>
  );
}
